import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { filteringPaymentMethods } from '../../../PaymentForm/utils';
import { ReactComponent as CloseIcon } from 'assets/iconButtons/new-close-icon.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/iconButtons/new-arrow-up-icon.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/iconButtons/new-arrow-down-icon.svg';
import { ReactComponent as CopyIcon } from 'assets/iconButtons/new-copy-icon.svg';
import { ReactComponent as ExportIcon } from 'assets/iconButtons/new-export-icon.svg';
import { ReactComponent as RepeatIcon } from 'assets/iconButtons/new-repeat-icon.svg';
import {
  CUSTOMER_PERMISSIONS,
  PAYMENT_METHOD,
  PAYMENT_PROVIDERS,
  TRANSACTION_TYPES,
  TYPES_OF_TRANSACTION
} from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { useMediaQuery } from 'services/useMediaQUery';
import { ROUTE_PATHS } from 'routes/constants';
import { MEDIA_QUERY } from 'services/utils';
import { formatPaymentDateSeparate } from 'services/utils';
import Button from 'uikit/Button/Button';
import './CryptoPaymentDetails.scss';

const CryptoPaymentDetails = ({
  transaction,
  amountDetails,
  commissionDetails,
  totalAmountDetails,
  onClose,
  accountNumber,
  userAccounts,
  paymentStore,
  transactionStore,
  customerPermissions,
  userStore
}) => {
  const navigate = useNavigate();
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const [copiedField, setCopiedField] = useState(null);

  const isReportDisabled = customerPermissions
    ? !customerPermissions?.find((permission) => permission === CUSTOMER_PERMISSIONS.CREATE_REPORT)
    : false;

  // Get crypto explorer link
  const cryptoExplorerLink = userStore.cryptoNetworksBaseUrl[transaction.network];

  // Format payment purpose with key-value display
  const getFormattedPurpose = () => {
    if (!transaction.purpose || !userStore?.paymentPurposes) {
      return transaction.purpose;
    }
    const purposeValue = userStore.paymentPurposes[transaction.purpose];
    return purposeValue ? `${transaction.purpose} - ${purposeValue}` : transaction.purpose;
  };
  const formattedPurpose = getFormattedPurpose();

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const repeatTransaction = () => {
    const senderAccount = userAccounts.find((userAccount) => userAccount.wallet_number === transaction.wallet_number);

    paymentStore.resetPaymentStore();
    paymentStore.setSelectedAccount(senderAccount);
    paymentStore.setPreviousTransactionInfo(transaction);
    paymentStore.setIsRepeatPaymentStatus(true);
    paymentStore.setPaymentMethod(PAYMENT_METHOD.CRYPTO);
    paymentStore.setAvailablePaymentMethods(
      filteringPaymentMethods(senderAccount?.payment_methods, senderAccount?.transfer_provider)
    );
    navigate(ROUTE_PATHS.NEW_PAYMENT);
  };

  const onPDFClick = (e) => {
    e.preventDefault();
    if (transaction?.is_pdf_available) {
      transactionStore.downloadCurrentPDF(transaction.account_number, transaction.transaction_number);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClassMap = {
      COMPLETED: 'status-completed',
      PAID: 'status-completed',
      IN_PROGRESS: 'status-in-progress',
      PROCESSING: 'status-in-progress',
      PENDING: 'status-pending',
      CREATED: 'status-pending',
      ON_HOLD: 'status-on-hold',
      FAILED: 'status-failed',
      REJECTED: 'status-failed',
      MONITORING: 'status-monitoring',
      CANCELLED: 'status-cancelled',
      ACCEPT_REQUESTED: 'status-requested',
      REJECT_REQUESTED: 'status-requested'
    };
    return statusClassMap[status] || 'status-default';
  };

  const createdDate = formatPaymentDateSeparate(transaction.created_at);
  const completedDate = formatPaymentDateSeparate(transaction.completed_at);
  const lastUpdatedDate = formatPaymentDateSeparate(transaction.updated_at);

  // Calculate amounts
  const isOutgoingTransaction = transaction.type === TRANSACTION_TYPES.OUTGOING;
  const isIncomingTransaction = transaction.type === TRANSACTION_TYPES.INCOMING;
  const isFailedOrRejected = transaction.status === 'FAILED' || transaction.status === 'REJECTED';
  const displayStatus = transaction.status === 'PAID' ? 'COMPLETED' : transaction.status;
  const amount = `${isOutgoingTransaction ? `-${transaction?.amountDetails?.displayAmount}` : isIncomingTransaction ? `+${transaction?.sourceAmountDetails?.displayAmount || transaction?.amountDetails?.displayAmount}` : ''}`;
  const commission = transaction?.totalCommissionDetails?.displayAmount;

  const renderMobile = () => (
    <div className='crypto-payment-details mobile'>
      <div className='modal-header'>
        <h2 className='modal-title'>{i18n.getMessage('popUp.paymentDetails')}</h2>
        <CloseIcon className='close-icon' onClick={onClose} />
      </div>

      <div className='modal-body mobile'>
        {/* Payment Summary */}
        <div className='payment-summary'>
          <div className='transaction-type-block'>
            <div className='transaction-type'>
              <div className='type-icon-wrapper'>
                {isIncomingTransaction ? (
                  <ArrowDownIcon className='type-icon-incoming' />
                ) : (
                  <ArrowUpIcon className='type-icon' />
                )}
              </div>
              <span className='type-label'>{i18n.getMessage(`transaction.details.type.${transaction.type}`)}</span>
            </div>
            <div className='transaction-date'>{`${createdDate.date}, ${createdDate.time}`}</div>
          </div>

          <div className='amount-status'>
            <span className={`status-badge ${getStatusBadgeClass(displayStatus)}`}>
              {i18n.getMessage(`transaction.details.status.${displayStatus}`)}
            </span>
            <div
              className={`transaction-amount ${isIncomingTransaction ? 'incoming' : ''} ${isFailedOrRejected ? 'failed' : ''}`}
            >
              {amount}
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className='details-section mobile'>
          <h3 className='section-title'>{i18n.getMessage('transaction.section.transactionDetails')}</h3>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.transactionHash')}</span>
            <div className='detail-value'>
              {transaction.crypto_hash ? (
                <a
                  href={`${cryptoExplorerLink}${transaction.crypto_hash}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='transaction-hash-link'
                >
                  {transaction.crypto_hash}
                </a>
              ) : (
                <span>{transaction.transaction_hash || transaction.comment}</span>
              )}
              <button
                className='copy-button'
                onClick={() =>
                  handleCopy(
                    transaction.crypto_hash || transaction.transaction_hash || transaction.comment,
                    'transactionHash'
                  )
                }
              >
                <CopyIcon />
              </button>
            </div>
          </div>

          {transaction.purpose && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.purpose')}</span>
              <div className='detail-value'>
                <span>{formattedPurpose}</span>
                <button className='copy-button' onClick={() => handleCopy(formattedPurpose, 'purpose')}>
                  <CopyIcon />
                </button>
              </div>
            </div>
          )}

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.commission')}</span>
            <div className='detail-value'>
              <span>{commission}</span>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.transactionId')}</span>
            <div className='detail-value'>
              <span>{transaction.transaction_number}</span>
              <button
                className='copy-button'
                onClick={() => handleCopy(transaction.transaction_number, 'transactionId')}
              >
                <CopyIcon />
              </button>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.paymentMethod')}</span>
            <div className='detail-value'>
              <span>{i18n.getMessage(`transfer.paymentMethod.${transaction.payment_method}`)}</span>
            </div>
          </div>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.dateCreated')}</span>
            <div className='detail-value'>
              <span>{`${createdDate.date}, ${createdDate.time}`}</span>
            </div>
          </div>

          {completedDate.date && completedDate.time && (
            <div className='detail-row'>
              <span className='detail-label'>{i18n.getMessage('transaction.label.dateCompleted')}</span>
              <div className='detail-value'>
                <span>{`${completedDate.date}, ${completedDate.time}`}</span>
              </div>
            </div>
          )}

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.lastUpdated')}</span>
            <div className='detail-value'>
              <span>{`${lastUpdatedDate.date}, ${lastUpdatedDate.time}`}</span>
            </div>
          </div>
        </div>

        {/* First Section - Sender for Incoming, Recipient for Outgoing */}
        <div className='details-section mobile'>
          <h3 className='section-title'>
            {i18n.getMessage(
              isIncomingTransaction ? 'transaction.section.senderDetails' : 'transaction.section.recipientDetails'
            )}
          </h3>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.walletAddress')}</span>
            <div className='detail-value'>
              <span>
                {isIncomingTransaction
                  ? transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || ''
                  : transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || ''}
              </span>
              <button
                className='copy-button'
                onClick={() =>
                  handleCopy(
                    isIncomingTransaction
                      ? transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || ''
                      : transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || '',
                    'senderWallet'
                  )
                }
              >
                <CopyIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Second Section - Recipient for Incoming, Sender for Outgoing */}
        <div className='details-section mobile'>
          <h3 className='section-title'>
            {i18n.getMessage(
              isIncomingTransaction ? 'transaction.section.recipientDetails' : 'transaction.section.senderDetails'
            )}
          </h3>

          <div className='detail-row'>
            <span className='detail-label'>{i18n.getMessage('transaction.label.walletAddress')}</span>
            <div className='detail-value'>
              <span>{isIncomingTransaction ? transaction.crypto_address || '' : transaction.crypto_address || ''}</span>
              <button
                className='copy-button'
                onClick={() =>
                  handleCopy(
                    isIncomingTransaction ? transaction.crypto_address || '' : transaction.crypto_address || '',
                    'recipientWallet'
                  )
                }
              >
                <CopyIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='action-buttons mobile'>
          <Button
            type='secondary'
            size='small'
            onClick={onPDFClick}
            isDisabled={isReportDisabled}
            className='export-button mobile'
          >
            <ExportIcon className='button-icon' />
            {i18n.getMessage('button.pdf')}
          </Button>

          {transaction.transaction_type === TYPES_OF_TRANSACTION.NORMAL &&
            transaction.type === TRANSACTION_TYPES.OUTGOING && (
              <Button type='primary' size='small' onClick={repeatTransaction} className='repeat-button mobile'>
                <RepeatIcon className='button-icon' />
                {i18n.getMessage('button.repeatPayment')}
              </Button>
            )}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return renderMobile();
  }

  return (
    <div className='crypto-payment-details'>
      <div className='modal-header'>
        <h2 className='modal-title'>{i18n.getMessage('popUp.paymentDetails')}</h2>
        <CloseIcon className='close-icon' onClick={onClose} />
      </div>

      <div className='modal-body'>
        {/* Transaction Header */}
        <div className='transaction-header'>
          <div className='transaction-type-block'>
            <div className='transaction-type'>
              <div className='type-icon-wrapper'>
                {isIncomingTransaction ? (
                  <ArrowDownIcon className='type-icon-incoming' />
                ) : (
                  <ArrowUpIcon className='type-icon' />
                )}
              </div>
              <div className='type-info'>
                <span className='type-label'>{i18n.getMessage(`transaction.details.type.${transaction.type}`)}</span>
              </div>
            </div>

            <div className='transaction-type'>
              <span className='type-date'>{`${createdDate.date}, ${createdDate.time}`}</span>
            </div>
          </div>

          <div className='transaction-status'>
            <span className={`status-badge ${getStatusBadgeClass(displayStatus)}`}>
              {i18n.getMessage(`transaction.details.status.${displayStatus}`)}
            </span>
            <div
              className={`transaction-amount ${isIncomingTransaction ? 'incoming' : ''} ${isFailedOrRejected ? 'failed' : ''}`}
            >
              {amount}
            </div>
          </div>
        </div>

        <div className='details-container'>
          {/* Transaction Details Section */}
          <div className='details-section transaction-details-section'>
            <div className='section-header'>
              <h3>{i18n.getMessage('transaction.section.transactionDetails')}</h3>
            </div>
            <div className='section-content'>
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.transactionHash')}</span>
                <div className='detail-value with-action crypto'>
                  {transaction.crypto_hash ? (
                    <a
                      href={`${cryptoExplorerLink}${transaction.crypto_hash}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='transaction-hash-link'
                    >
                      {transaction.crypto_hash}
                    </a>
                  ) : (
                    <span>{transaction.transaction_hash || transaction.comment}</span>
                  )}
                  <button
                    className='copy-button'
                    onClick={() =>
                      handleCopy(
                        transaction.crypto_hash || transaction.transaction_hash || transaction.comment,
                        'transactionHash'
                      )
                    }
                    title={i18n.getMessage('button.copy')}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>

              {transaction.purpose && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.purpose')}</span>
                  <div className='detail-value with-action'>
                    <span>{formattedPurpose}</span>
                    <button
                      className='copy-button'
                      onClick={() => handleCopy(formattedPurpose, 'purpose')}
                      title={i18n.getMessage('button.copy')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              )}

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.commission')}</span>
                <div className='detail-value'>
                  <span>{commission}</span>
                </div>
              </div>

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.transactionId')}</span>
                <div className='detail-value with-action'>
                  <span>{transaction.transaction_number}</span>
                  <button
                    className='copy-button'
                    onClick={() => handleCopy(transaction.transaction_number, 'transactionId')}
                    title={i18n.getMessage('button.copy')}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.paymentMethod')}</span>
                <div className='detail-value'>
                  <span>{i18n.getMessage(`transfer.paymentMethod.${transaction.payment_method}`)}</span>
                </div>
              </div>

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.dateCreated')}</span>
                <div className='detail-value'>
                  <span>{`${createdDate.date}, ${createdDate.time}`}</span>
                </div>
              </div>

              {completedDate.date && completedDate.time && (
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.dateCompleted')}</span>
                  <div className='detail-value'>
                    <span>{`${completedDate.date}, ${completedDate.time}`}</span>
                  </div>
                </div>
              )}

              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('transaction.label.lastUpdated')}</span>
                <div className='detail-value'>
                  <span>{`${lastUpdatedDate.date}, ${lastUpdatedDate.time}`}</span>
                </div>
              </div>
            </div>
          </div>

          <div className='details-right-column'>
            {/* First Section - Sender for Incoming, Recipient for Outgoing */}
            <div className='details-section'>
              <div className='section-header'>
                <h3>
                  {i18n.getMessage(
                    isIncomingTransaction ? 'transaction.section.senderDetails' : 'transaction.section.recipientDetails'
                  )}
                </h3>
              </div>
              <div className='section-content'>
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.walletAddress')}</span>
                  <div className='detail-value with-action crypto'>
                    <span>
                      {isIncomingTransaction
                        ? transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || ''
                        : transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || ''}
                    </span>
                    <button
                      className='copy-button'
                      onClick={() =>
                        handleCopy(
                          isIncomingTransaction
                            ? transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || ''
                            : transaction.counterparty?.crypto_wallet || transaction.recipient_wallet_number || '',
                          'senderWallet'
                        )
                      }
                      title={i18n.getMessage('button.copy')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Section - Recipient for Incoming, Sender for Outgoing */}
            <div className='details-section'>
              <div className='section-header'>
                <h3>
                  {i18n.getMessage(
                    isIncomingTransaction ? 'transaction.section.recipientDetails' : 'transaction.section.senderDetails'
                  )}
                </h3>
              </div>
              <div className='section-content'>
                <div className='detail-row'>
                  <span className='detail-label'>{i18n.getMessage('transaction.label.walletAddress')}</span>
                  <div className='detail-value with-action crypto'>
                    <span>
                      {isIncomingTransaction ? transaction.crypto_address || '' : transaction.crypto_address || ''}
                    </span>
                    <button
                      className='copy-button'
                      onClick={() =>
                        handleCopy(
                          isIncomingTransaction ? transaction.crypto_address || '' : transaction.crypto_address || '',
                          'recipientWallet'
                        )
                      }
                      title={i18n.getMessage('button.copy')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='action-buttons'>
          <Button
            type='secondary'
            size='large'
            onClick={onPDFClick}
            isDisabled={isReportDisabled}
            className='export-button'
          >
            <ExportIcon className='button-icon' />
            {i18n.getMessage('button.exportPdf')}
          </Button>

          {transaction.transaction_type === TYPES_OF_TRANSACTION.NORMAL &&
            transaction.type === TRANSACTION_TYPES.OUTGOING && (
              <Button type='primary' size='large' onClick={repeatTransaction} className='repeat-button'>
                <RepeatIcon className='button-icon' />
                {i18n.getMessage('button.repeatPayment')}
              </Button>
            )}
        </div>
      </div>
    </div>
  );
};

CryptoPaymentDetails.propTypes = {
  transaction: PropTypes.object.isRequired,
  amountDetails: PropTypes.object,
  commissionDetails: PropTypes.object,
  totalAmountDetails: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject,
  transactionStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject,
  customerPermissions: PropTypes.array,
  accountNumber: PropTypes.string
};

export default inject((stores) => ({
  accountNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore?.userAccounts,
  paymentStore: stores.paymentStore,
  transactionStore: stores.transactionsStore,
  customerPermissions: stores.userStore?.currentCustomerPermissions,
  userStore: stores.userStore
}))(observer(CryptoPaymentDetails));
